import React from 'react';
import { Box, Button, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import { CloudUpload as CloudUploadIcon, Delete as DeleteIcon } from '@mui/icons-material';
import apiService from '../services/api';
import { getApiOrigin } from '../config/environment';

type Props = {
  label: string;
  value: string;
  onChange: (url: string) => void;
};

const ALLOWED_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp']);

function resolveImageUrl(path: string): string {
  const trimmed = path.trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `${getApiOrigin()}${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
}

const SplashCoverUploadField: React.FC<Props> = ({ label, value, onChange }) => {
  const inputRef = React.useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (!ALLOWED_TYPES.has(file.type)) {
      setError('Formats acceptés : JPG, PNG ou WebP.');
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      setError('Le fichier ne doit pas dépasser 4 Mo.');
      return;
    }

    setError(null);
    setUploading(true);
    try {
      const response = await apiService.uploadHomeSplashScreenCover(file);
      if (response.success && response.image?.url) {
        onChange(response.image.url);
      } else {
        setError('Erreur lors de l\'upload.');
      }
    } catch (err: unknown) {
      const message = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      setError(message || 'Erreur lors de l\'upload.');
    } finally {
      setUploading(false);
    }
  };

  const previewUrl = value ? resolveImageUrl(value) : '';

  return (
    <Box>
      <Typography variant="subtitle2" gutterBottom>
        {label}
      </Typography>
      {previewUrl ? (
        <Box
          sx={{
            mb: 1.5,
            borderRadius: 1.5,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: 'divider',
            maxWidth: 320,
            height: 140,
            position: 'relative',
            bgcolor: '#f1f5f9',
          }}
        >
          <Box component="img" src={previewUrl} alt="" sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        </Box>
      ) : null}
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Button
          variant="outlined"
          size="small"
          startIcon={uploading ? <CircularProgress size={16} /> : <CloudUploadIcon />}
          onClick={() => inputRef.current?.click()}
          disabled={uploading}
        >
          {value ? 'Remplacer la photo' : 'Uploader une photo'}
        </Button>
        {value ? (
          <IconButton size="small" aria-label="Supprimer la couverture" onClick={() => onChange('')}>
            <DeleteIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Stack>
      {error ? (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
          {error}
        </Typography>
      ) : (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          Le logo du partenaire s&apos;affichera centré par-dessus.
        </Typography>
      )}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={handleUpload} />
    </Box>
  );
};

export default SplashCoverUploadField;
